import { parseMoneyToCents, tt } from '@agos/shared'

/**
 * The *Sprievodný list* a driver carries — §9.108, `BACKEND.md` §10A.35's `print-note`.
 *
 * <p><b>Ported line for line from legacy's `buildDeliveryNoteLines`.</b> A driver reads this at speed
 * in a car, and changing its shape would change what somebody is used to for no reason. The rule, the
 * order number, the date, the časenka, the customer, a picking summary, the items, the money, the
 * payment method, the note, the driver.
 *
 * <p><b>It is not a fiscal document.</b> `print-note` is documented as *"a slip that is not a fiscal
 * document"* — nothing is registered and no receipt is issued, which is why it carries no VAT
 * recapitulation and no document number.
 *
 * <p><b>Every field is optional and each is omitted rather than printed empty.</b> A line reading
 * `Adr: undefined` on a slip somebody is using to find a house is worse than no line.
 */

/**
 * Thirty-two columns, which is what the register's paper takes.
 *
 * <p><b>The server truncates at forty.</b> `FiskalDocument.NAME_WIDTH` is 40 and `clean()` cuts to it,
 * so a long address or product name is shortened on the slip rather than wrapped — legacy behaves the
 * same way. The rules are 32 so they sit inside that with room to spare.
 */
const RULE = '================================'
const THIN = '--------------------------------'

export interface NoteItem {
  productName?: string
  quantity?: number
  unitPrice?: number | string
  totalPrice?: number | string
  categoryName?: string | null
}

export interface NoteOrder {
  orderNumber?: string
  createdAt?: string
  scheduledAt?: string | null
  customerName?: string
  customerPhone?: string
  customerAddress?: string | null
  total?: number | string
  deliveryCost?: number | string
  discount?: number | string
  paymentMethod?: string
  notes?: string | null
  driverName?: string
  items?: NoteItem[]
}

const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Hotovost',
  card_terminal: tt('Karta terminal'),
  card_courier: tt('Karta kurier'),
  card_online: tt('Online platba'),
  card: tt('Karta'),
}

/**
 * The three groups the summary calls out.
 *
 * <p>Legacy's `getCategoryProductSummary` singles these out because they are what gets left behind:
 * a pizza in the bag is obvious, a missing dressing is not. Matched on the category name because that
 * is what the order line carries.
 */
const SUMMARY_GROUPS = [/^prílohy|^prilohy/i, /^dresing/i, /^nápoj|^napoj/i]

function money(value: number | string | undefined): string {
  const amount = Number(value ?? 0)
  // N-02 is about screens. This string is a register command: `FiskalDocument.note` joins its parts
  // with a comma and `clean()` replaces every comma in a line with a space, so a Slovak decimal comma
  // would print as `24 50 EUR`. Legacy writes a dot for the same reason.
  // eslint-disable-next-line no-restricted-syntax
  return `${(Number.isFinite(amount) ? amount : 0).toFixed(2)} EUR`
}

function positive(value: number | string | undefined): boolean {
  return Number(value ?? 0) > 0
}

/** `dd.mm.yyyy hh:mm` in the restaurant's own locale, blank when there is no date to print. */
function stamp(iso: string | undefined | null, withWeekday = false): string {
  if (!iso) return ''
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ''
  // N-01 is honoured rather than dodged — the zone is pinned on the next line. The `@agos/shared`
  // helpers format for a screen; this is a 40-column printer line with its own shape.
  // eslint-disable-next-line no-restricted-syntax
  return new Intl.DateTimeFormat('sk-SK', {
    // **Pinned, and this is the half of the port that legacy got wrong.** Legacy omits `timeZone`, so
    // the slip carries whatever zone the manager's machine is on — a laptop left on UTC prints a
    // delivery two hours early. The restaurant's day is a Bratislava day.
    timeZone: 'Europe/Bratislava',
    ...(withWeekday ? { weekday: 'long' as const } : { year: '2-digit' as const }),
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(when)
}

/** Sides, dressings and drinks, in that order, as `2x Hranolky`. */
export function summaryLines(items: readonly NoteItem[]): string[] {
  const lines: string[] = []
  for (const group of SUMMARY_GROUPS) {
    for (const item of items) {
      if (group.test((item.categoryName ?? '').trim())) {
        lines.push(`  ${item.quantity ?? 0}x ${item.productName ?? ''}`)
      }
    }
  }
  return lines
}

export function deliveryNoteLines(order: NoteOrder): string[] {
  const items = order.items ?? []
  const lines: string[] = [RULE, tt('    SPRIEVODNÝ LIST'), RULE]

  lines.push(`Obj: ${order.orderNumber ?? ''}`)
  const created = stamp(order.createdAt)
  if (created !== '') lines.push(`${tt('Dátum: ')}${created}`)
  const scheduled = stamp(order.scheduledAt, true)
  if (scheduled !== '') lines.push(`${tt('Časenka: ')}${scheduled}`)

  lines.push(THIN)
  const hasCustomer =
    Boolean(order.customerName) || Boolean(order.customerPhone) || Boolean(order.customerAddress)
  if (order.customerName) lines.push(order.customerName)
  if (order.customerPhone) lines.push(`Tel: ${order.customerPhone}`)
  if (order.customerAddress) lines.push(`Adr: ${order.customerAddress}`)
  if (hasCustomer) lines.push(THIN)

  const summary = summaryLines(items)
  if (summary.length > 0) {
    if (!hasCustomer) lines.push(THIN)
    lines.push(tt('SÚHRN:'))
    lines.push(...summary)
    lines.push(THIN)
  }

  for (const item of items) {
    // `totalPrice` where the server sent one, the multiplication where it did not — legacy's rule,
    // and the two disagree on a discounted line.
    const price =
      item.totalPrice ?? Number(item.unitPrice ?? 0) * Number(item.quantity ?? 0)
    lines.push(`${item.quantity ?? 0}x ${item.productName ?? ''}`)
    lines.push(`   ${money(price)}`)
  }

  if (positive(order.deliveryCost)) lines.push(`Doprava: ${money(order.deliveryCost)}`)
  if (positive(order.discount)) lines.push(`${tt('Zľava: -')}${money(order.discount)}`)

  lines.push(THIN)
  lines.push(`SPOLU: ${money(order.total)}`)
  lines.push(`Platba: ${PAYMENT_LABELS[order.paymentMethod ?? ''] ?? order.paymentMethod ?? ''}`)
  if (order.notes) lines.push(`Pozn: ${order.notes}`)
  if (order.driverName) lines.push(`${tt('Šofér: ')}${order.driverName}`)
  lines.push(RULE)

  return lines
}

/** The body `POST /fiskal/print-note` reads. Delivery's register, as legacy sends. */
export function printNotePayload(order: NoteOrder): { kasaId: string; lines: string[] } {
  return { kasaId: 'rozvoz', lines: deliveryNoteLines(order) }
}

/**
 * What the toolbar says after printing a batch.
 *
 * <p><b>A failure is named, not folded into a total.</b> The register is reachable or it is not, and
 * "3 z 5" with no list leaves the dispatcher guessing which two drivers are about to leave without a
 * slip.
 */
export function printOutcomeNote(printed: number, failed: readonly string[]): string {
  if (failed.length === 0) {
    return `${tt('Vytlačené sprievodné listy:')} ${printed}`
  }
  return `${tt('Vytlačené:')} ${printed} · ${tt('nevytlačené:')} ${failed.join(', ')}`
}

/**
 * The receipt request for one delivery order — §9.123, the owner's point 9.
 *
 * <p><b>This issues a real tax document.</b> `BLOCKED.md` requires `POST /fiskal/receipt` to be
 * intercepted in every e2e, which is why the payload is built here as a pure value rather than inline
 * at the call: what is sent to a register is worth being able to read and test without sending it.
 *
 * <p><b>No `vatRate` is sent.</b> `BACKEND.md §10A.67` made the product catalogue outrank whatever
 * the client claims, precisely because clients disagreed about whether a channel is eaten in. Sending
 * a rate here would be ignored at best and a contradiction in the record at worst.
 *
 * <p><b>`kasaId` is the delivery register</b>, as legacy sends from this board — a dispatch screen is
 * the rozvoz till by definition.
 *
 * <p><b>Answers `null` where the order does not say how it was paid.</b> A tax document states the
 * payment method; defaulting it would print a guess on a real document. The caller names the order
 * as a failure instead, which is what the dispatcher needs in order to go and look at it.
 */
export function receiptPayloadFor(order: NoteOrder & { id?: number; type?: string }): {
  kasaId: string
  orderId: number
  orderNumber: string
  orderType?: string
  paymentMethod: string
  total: number
  discount?: number
  deliveryCost?: number
  items: { name: string; quantity: number; unitPrice: number }[]
} | null {
  const money = (value: number | string | undefined): number =>
    value === undefined ? 0 : parseMoneyToCents(value) / 100
  const paymentMethod = (order.paymentMethod ?? '').trim()
  if (paymentMethod === '') return null
  const discount = money(order.discount)
  const deliveryCost = money(order.deliveryCost)
  return {
    kasaId: 'rozvoz',
    orderId: order.id ?? 0,
    orderNumber: order.orderNumber ?? '',
    ...(order.type === undefined ? {} : { orderType: order.type }),
    paymentMethod,
    total: money(order.total),
    // Omitted rather than sent as zero: a nil discount is not a discount of nothing.
    ...(discount > 0 ? { discount } : {}),
    ...(deliveryCost > 0 ? { deliveryCost } : {}),
    items: (order.items ?? []).map((item) => ({
      name: (item.productName ?? '').trim(),
      quantity: item.quantity ?? 0,
      unitPrice: money(item.unitPrice),
    })),
  }
}

/**
 * What the toolbar says after issuing a batch of receipts.
 *
 * <p>Named failures for the same reason `printOutcomeNote` names them, only harder: an unissued
 * receipt is a sale with no tax document, and the dispatcher has to know which order to chase.
 */
export function receiptOutcomeNote(
  issued: number,
  failed: readonly string[],
  /**
   * The orders whose document the server said **already exists** - §9.362, FB-649.
   *
   * A third bucket, because it belongs in neither of the other two. Counting these as failures is
   * what this function's own javadoc warns against - *the dispatcher has to know which order to
   * chase* - and an order with a receipt on the tray is the one order there is nothing to chase
   * about. Counting them as issued would be true of the document and false of this batch, and a
   * dispatcher comparing the number against how many they selected would be left guessing.
   *
   * Optional so the two callers that have no such case, and the tests written before it existed,
   * read the same as before.
   */
  alreadyHad: readonly string[] = [],
): string {
  const already =
    alreadyHad.length === 0 ? '' : `, ${alreadyHad.length} ${tt('už malo doklad')}`
  if (failed.length === 0) return `${tt('Vytlačené fiškálne doklady:')} ${issued}${already}`
  return `${issued} ${tt('vytlačené')}${already}, ${tt('zlyhalo')}: ${failed.join(', ')}`
}
